import { View, StyleSheet } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import React, { useEffect, useState } from "react";
import { UserCirclePlus } from "../../../components/svgs/common";
import UsersTable, { UsersTableProps } from "./UsersTable";
import {
  GetUsersForSchoolDocument,
  useAssignCourseToFacultyMutation,
  useGetUsersForSchoolQuery,
  useInviteUserToSchoolMutation,
  useRemoveCourseAssignmentFromFacultyMutation,
  useRevokeFacultyMutation,
  useSchoolsQuery,
} from "../../../graphql/generated/graphql";

import NewCustomAxioActionModal from "./NewCustomAxioActionModal";
import AssignCourseToFacultyListContainer from "./AssignCourseToFacultyListContainer";
import RemoveCourseFromFacultyListContainer from "./RemoveCourseFromFacultyList";
import InviteFacultyModalContainer from "./InviteFacultyModalContainer";
import { NewCustomActionButton } from "../../../components/common/general/CustomButton/NewCustomActionButton";
import CustomSearchInput from "../../../components/common/general/CustomSearchInput/CustomSearchInput";
import usePagination from "../../../hooks/usePagination";

const modals = {
  revokeFaculty: {
    title: "Remove Teacher from Platform?",
    ariaLabel: "Remove Teacher Modal",
    warningText:
      "Following faculty will no longer have access to the platform. This action cannot be undone, and all associated data will be deleted.",
    actionButtonText: "Remove Teacher",
  },
  revokeConfirmation: {
    title: "Success!",
    ariaLabel: "Revoke Confirmation Modal",
    warningText: "The faculty has been successfully revoked.",
    actionButtonText: "OK",
  },
};

export enum UsersTabModalState {
  CLOSED = "CLOSED",
  REVOKE = "REVOKE",
  REVOKE_CONFIRMATION = "REVOKE_CONFIRMATION",
  ASSIGN_COURSE = "ASSIGN_COURSE",
  REMOVE_COURSE = "REMOVE_COURSE",
  INVITE = "INVITE",
}

const UsersTabContainer = ({ customColor }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalState, setModalState] = useState({
    state: UsersTabModalState.CLOSED,
    facultyId: "",
    facultyName: "",
  });

  const { data: schoolsData, loading: schoolsLoading } = useSchoolsQuery();

  const [totalItemsCount, setTotalItemsCount] = useState(0);

  const { page, setPage, pageSize, PaginationComponent } = usePagination({
    totalItemsCount,
  });

  const { data: usersData, refetch: usersRefetch } = useGetUsersForSchoolQuery({
    variables: {
      nameOrEmail: searchQuery,
      schoolId: schoolsData?.getSchools[0].id,
      page,
      pageSize,
    },
  });

  useEffect(() => {
    setTotalItemsCount(usersData?.getUsersForSchool?.data.totalCount || 0);
  }, [usersData]);

  const refetchQueries = [
    {
      query: GetUsersForSchoolDocument,
      variables: {
        nameOrEmail: searchQuery,
        schoolId: schoolsData?.getSchools[0].id,
        page: page,
        pageSize: pageSize,
      },
    },
  ];

  const [assignCourseToFaculty] = useAssignCourseToFacultyMutation();
  const [removeCourseFromFaculty] = useRemoveCourseAssignmentFromFacultyMutation();
  const [inviteUserToSchool] = useInviteUserToSchoolMutation();
  const [revokeFaculty] = useRevokeFacultyMutation();

  const inviteMember = async ({
    email,
    name,
    role,
  }: {
    email: string;
    name: string;
    role: "School Admin" | "Faculty" | "Auditor";
  }) => {
    setIsSubmitting(true);
    try {
      const result = await inviteUserToSchool({
        variables: {
          input: {
            email: email,
            name: name,
            role: role,
          },
        },
        refetchQueries: refetchQueries,
      });
      setIsSubmitting(false);
      return result.data?.inviteUserToSchool?.success;
    } catch (error) {
      setIsSubmitting(false);
      return false;
    }
  };

  const assignCourse = async (courseId: string) => {
    await assignCourseToFaculty({
      variables: {
        userId: modalState.facultyId,
        courseId: courseId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({ state: UsersTabModalState.CLOSED, facultyId: "", facultyName: "" });
  };

  const removeCourse = async (courseId: string) => {
    await removeCourseFromFaculty({
      variables: {
        userId: modalState.facultyId,
        courseId: courseId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({ state: UsersTabModalState.CLOSED, facultyId: "", facultyName: "" });
  };

  const handleRevokeFaculty = async () => {
    await revokeFaculty({
      variables: {
        userId: modalState.facultyId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({ state: UsersTabModalState.REVOKE_CONFIRMATION, facultyId: "", facultyName: "" });
    setSearchQuery("");
  };

  const handleActionModalOpen = async (facultyId: string, facultyName: string, modalType: UsersTabModalState) => {
    setModalState({ state: modalType, facultyId, facultyName });
  };

  const handleModalClose = () => {
    setModalState({ state: UsersTabModalState.CLOSED, facultyId: "", facultyName: "" });
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    usersRefetch();
  }, [searchQuery, page, pageSize]);

  return (
    <>
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <CustomText text="Users" size="l" weight="bold" style={styles.headerText} />
            <NewCustomActionButton
              text="+Invite "
              onPress={() => setModalState({ state: UsersTabModalState.INVITE, facultyId: "", facultyName: "" })}
              leftIcon={<UserCirclePlus fill={customColor} />}
            />
          </View>
          <View style={styles.headerRow}>
            <View style={styles.subHeaderContainer}>
              <CustomText text="Total Users" size="m" weight="semiBold" style={styles.subHeaderText} />
              <CustomText
                text={`(${usersData?.getUsersForSchool.data.totalCount || 0})`}
                size="m"
                weight="semiBold"
                style={styles.subHeaderText}
                useTranslationText={false}
              />
            </View>
            <CustomSearchInput searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </View>
        </View>
        <UsersTable
          faculties={(usersData?.getUsersForSchool?.data.faculties || []) as unknown as UsersTableProps["faculties"]}
          onActionModalOpen={handleActionModalOpen}
        />
        {PaginationComponent}
      </View>
      <NewCustomAxioActionModal
        visible={modalState.state === UsersTabModalState.REVOKE}
        onClose={handleModalClose}
        onAction={handleRevokeFaculty}
        modalTexts={modals.revokeFaculty}
        contextualText={modalState.facultyName}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === UsersTabModalState.REVOKE_CONFIRMATION}
        onClose={handleModalClose}
        modalTexts={modals.revokeConfirmation}
        contextualText={modalState.facultyName}
      />
      <AssignCourseToFacultyListContainer
        visible={modalState.state === UsersTabModalState.ASSIGN_COURSE}
        onClose={handleModalClose}
        onAction={assignCourse}
      />
      <RemoveCourseFromFacultyListContainer
        visible={modalState.state === UsersTabModalState.REMOVE_COURSE}
        onClose={handleModalClose}
        onAction={removeCourse}
        userId={modalState.facultyId}
      />
      <InviteFacultyModalContainer
        visible={modalState.state === UsersTabModalState.INVITE}
        onClose={handleModalClose}
        onSubmit={inviteMember}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
    backgroundColor: "#fff",
    borderRadius: 8,
    flex: 1,
  },
  header: {
    flexDirection: "column",
    width: "100%",
    marginBottom: 24,
    gap: 24,
  },
  headerRow: {
    flexDirection: "row",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
  },
  subHeaderContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  headerText: {
    color: "#000",
  },
  subHeaderText: {
    color: "#000",
  },
});

export default UsersTabContainer;
